import * as React from 'react';
import { inject } from 'mobx-react';

import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { UitkFauxInput, UitkTextInput } from 'uitk-react-input';
import { renderDatePicker, UitkDateFieldProps } from 'uitk-react-date-picker';

import { UitkDialogAnimationType, UitkFullscreenDialog } from 'uitk-react-dialog';

import { Datepicker } from 'components/common/date-picker';

export interface DateFieldProps {
  context?: Context;
  analytics?: AnalyticsStore;
  registerFocusMethod: (fieldName: string, focusMethod: () => void) => void;
  rfrr: string;
  routeStore?: RouteStore;
  fetchDatePickerRates?: () => Promise<object>;
  isDateless?: boolean;
}

export type LocalizedDateFieldProps = LocalizedComponentProps & UitkDateFieldProps & DateFieldProps;

export interface DateFieldState {
  mounted: boolean;
  textValues: any;
}

@withLocalization
@inject('routeStore', 'analytics', 'context')
export class DateField extends React.Component<LocalizedDateFieldProps, DateFieldState> {
  constructor(props) {
    super(props);

    this.focusButton = React.createRef();

    const { buttonText } = props;

    const textValues: any = {};

    if (buttonText) {
      textValues.buttonText = buttonText;
    }

    this.state = {
      mounted: false,
      textValues
    };

    this.dismiss = this.dismiss.bind(this);
    this.focusDateFieldInput = this.focusDateFieldInput.bind(this);
    this.open = this.open.bind(this);
    this.renderDatePicker = renderDatePicker.bind(this);
    this.save = this.save.bind(this);
  }

  public renderDatePicker;
  public focusButton = null;
  private datePickerAnalytics = {};
  private controls = {};

  public static defaultProps = {
    endDate: '',
    startDate: '',
    l10n: null
  };

  public componentDidMount() {
    const { name, registerFocusMethod } = this.props;

    registerFocusMethod(name, this.focusDateFieldInput);

    this.setState({ mounted: true });
  }

  public open() {
    const { analytics, rfrr, routeStore, dateType } = this.props;

    routeStore.updateSearch({ pwaDialog: `datepicker-${dateType}` });

    if (analytics && typeof analytics.trackEvent === 'function' && rfrr) {
      analytics.trackEvent(rfrr, 'Open Date Picker', analytics.omnitureData);
    }
  }

  public save(start, end) {
    const { l10n, onChange } = this.props;

    const { formatDate } = l10n;

    onChange({
      target: {
        name: 'startDate',
        value: formatDate(start, { raw: 'yyyy-MM-dd' })
      }
    });

    onChange({
      target: {
        name: 'endDate',
        value: formatDate(end, { raw: 'yyyy-MM-dd' })
      }
    });

    this.dismiss();
  }

  public focusDateFieldInput(): void {
    // manage focus back to the trigger
    const focusButton = this.focusButton && this.focusButton.current;
    if (focusButton && typeof focusButton.focus === 'function') {
      focusButton.focus();
    }
  }

  public dismiss() {
    this.props.routeStore.goBack();
    this.focusDateFieldInput();
  }

  public render() {
    const { analytics, dateType, endDate, id, invalid, l10n, name, onChange, startDate, fetchDatePickerRates, context } = this.props;
    let { placeholder } = this.props;

    const { mounted } = this.state;

    const { formatDateString } = l10n;

    const idBtn = `${id}__btn`;
    const value = dateType === 'start' ? startDate : endDate;
    const formattedBtnText = value ? formatDateString(value, { skeleton: 'MMMd' }) : placeholder;
    const accessibleFormattedText = value ? formatDateString(value, { skeleton: 'yMMMMd' }) : placeholder;

    if (!mounted) {
      placeholder = context.localDateFormat;
    }
    const label = () => (
      <span>
        {this.props.label}
        {!mounted && (
          <span className="is-visually-hidden" data-stid="date-field-label">
            {accessibleFormattedText}
          </span>
        )}
      </span>
    );

    const classNames = [];
    if (mounted) {
      classNames.push('is-hidden');
    }

    this.datePickerAnalytics = {
      closeAnalytics: analytics.setupTracker(':ChangeDatesClose', 'Close Date Picker', analytics.omnitureData),
      doneAnalytics: this.props.isDateless
        ? analytics.setupTracker(':DatepickerSearch', 'Search Date Picker', analytics.omnitureData)
        : analytics.setupTracker(':ChangeDatesDone', 'Apply Date Picker', analytics.omnitureData)
    };

    this.controls = {
      dismiss: this.dismiss,
      save: this.save
    };

    return (
      <>
        <UitkTextInput
          className={classNames.join(' ')}
          data-stid="input-date"
          icon="today"
          id={id}
          invalid={invalid}
          label={label()}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          value={value}
        >
          {mounted && (
            <UitkFauxInput data-stid="open-date-picker" domRef={this.focusButton} id={idBtn} onClick={this.open} type="button">
              <span className="is-visually-hidden" data-stid="date-field-label">
                {this.props.label} {accessibleFormattedText}
              </span>
              <span aria-hidden="true" data-stid={`${id}__dateLabel`}>
                {formattedBtnText}
              </span>
            </UitkFauxInput>
          )}
        </UitkTextInput>
        <QueryRoute query={{ pwaDialog: `datepicker-${dateType}` }}>
          <Layer id={`home-datepicker-${dateType}`} onDismiss={this.dismiss}>
            <UitkFullscreenDialog dialogShow={true} animation={UitkDialogAnimationType.SLIDEUP}>
              <Datepicker
                id={this.props.id}
                label={this.props.label}
                startDate={this.props.startDate}
                endDate={this.props.endDate}
                numDaysBookableInAdvance={this.props.numDaysBookableInAdvance}
                midnightBookingOffsetHours={this.props.midnightBookingOffsetHours}
                dateType={this.props.dateType}
                datePickerAnalytics={this.datePickerAnalytics}
                controls={this.controls}
                textValues={this.state.textValues}
                fetchDatePickerRates={fetchDatePickerRates}
              />
            </UitkFullscreenDialog>
          </Layer>
        </QueryRoute>
      </>
    );
  }
}
